import type { Quote } from '@client/model/quotesModel';
import {
  createQuoteWithROIandProjectedCoverage,
  createTraditionalLTCQuoteWithROIandProjectedCoverage,
} from '@client/util/policyQuoteCalculations';
import { adjustQuoteForCoverageRatio } from '@client/util/quoteAdjustment';
import { formatROI } from '@shared';

interface EnrichQuotesOptions {
  includeOriginalROIValues?: boolean;
  handleTraditionalLTC?: boolean;
}

export interface EnrichedQuote extends Quote {
  roi?: number;
  roiValues?: any;
  formattedROI?: string;
  originalROIValues?: {
    projectedReturnOnInvestment?: number;
    projectedCompoundAnnualGrowthRate?: number;
    projectedInternalRateOfReturn?: number;
    investmentPeriodYears?: number;
  };
}

/**
 * Enriches quotes with ROI calculations and formatted ROI values.
 * Handles both regular and traditional LTC quotes with error handling.
 * Auto-adjusts EquiTrust quotes to max coverage ratio for best LTC benefits.
 *
 * @param quotes - Array of quotes to enrich
 * @param client - Client data for ROI calculations
 * @param calculationType - Type of ROI calculation to perform
 * @param options - Additional options for enrichment
 * @returns Array of enriched quotes (quotes with calculation errors are filtered out)
 */
export function enrichQuotesWithROI(
  quotes: Quote[],
  client: Client,
  calculationType: ReturnOnInvestmentCalculationType,
  options: EnrichQuotesOptions = {},
): EnrichedQuote[] {
  const { includeOriginalROIValues = false, handleTraditionalLTC = false } = options;

  return quotes
    .map<EnrichedQuote | null>(quote => {
      try {
        // Check if this is a traditional LTC quote (only if enabled)
        const isTraditionalLTC = handleTraditionalLTC && !!(quote as any).traditionalLTCQuoteId;

        // Auto-adjust EquiTrust quotes to max coverage ratio for best LTC benefits
        const coverageAdjusted =
          !isTraditionalLTC && quote.maxCoverageRatio && quote.coverageRatio
            ? adjustQuoteForCoverageRatio(quote, quote.maxCoverageRatio)
            : quote;

        let enriched: any;

        if (isTraditionalLTC) {
          enriched = createTraditionalLTCQuoteWithROIandProjectedCoverage(
            coverageAdjusted as any,
            client,
            calculationType,
          );
        } else {
          enriched = createQuoteWithROIandProjectedCoverage(
            coverageAdjusted,
            client,
            calculationType,
          );
        }

        const formattedROI = formatROI(enriched.roiValues, calculationType);

        const result: EnrichedQuote = {
          ...enriched,
          formattedROI,
        };

        // Optionally include original ROI values for preservation
        if (includeOriginalROIValues && enriched.roiValues) {
          result.originalROIValues = {
            projectedReturnOnInvestment: enriched.roiValues.projectedReturnOnInvestment,
            projectedCompoundAnnualGrowthRate: enriched.roiValues.projectedCompoundAnnualGrowthRate,
            projectedInternalRateOfReturn: enriched.roiValues.projectedInternalRateOfReturn,
            investmentPeriodYears: enriched.roiValues.investmentPeriodYears,
          };
        }

        return result;
      } catch (error) {
        const quoteId = (quote as any).insuranceQuoteId || (quote as any).traditionalLTCQuoteId;
        console.error('Error calculating ROI for quote:', quoteId, error);
        return null;
      }
    })
    .filter((q): q is EnrichedQuote => q !== null);
}

